import React, { useEffect } from 'react';
import styled from 'styled-components';
import { useEditMode } from '../../shared/hooks/useEditMode';
import { useNavigate, useParams } from 'react-router-dom';
import { useProjectElementActions, useProjectElementState } from '../../shared/hooks/useApi/useProjectElement';
import { useProjectDetailState } from '../../shared/hooks/useApi/useProjectDetail';
import { PaperBlurFitBtn } from '../../shared/component/headless/button/BtnBody';
import { ProjectElementType, ModalType, DeviceType, AlertPosition, AlertType, DetailType, TextBlockType } from '../../shared/enum/EnumRepository';
import { CreateProjectElementReq } from '../../shared/dto/ReqDtoRepository';
import ProjectElement from '../../component/projectElement/ProjectElement';
import HeadlessBtn from '../../shared/component/headless/button/HeadlessBtn';
import { useModalStore } from '../../shared/store/portal/modalStore';
import { peCarouselBuilder, peDividerBuilder, peFormationBuilder, peTextBoxBuilder } from '../../shared/converter/entityBuilder';
import reorderIcon from '../../asset/icon/reorder.png';
import createIcon from '../../asset/icon/create.png';
import downloadIcon from '../../asset/icon/download.png';
import confirmIcon from '../../asset/icon/confirm2.png';
import confirmIcon2 from '../../asset/icon/confirm3.png';
import HintWrapper from '../../shared/component/headless/button/HintWrapper';
import { getHintMessage } from '../../shared/constants/hintMessages';
import { useDeviceStore } from '../../shared/store/deviceStore';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import MobileContextToolbar from '../../shared/component/MobileContextToolbar';
import { paperTextureAlert } from '../../shared/component/paperTexture';
import { getBtnName } from '../../shared/constants/btnNames';
import { BULK_UPLOAD_IMAGE_MAX_COUNT, DOCUMENT_DESCRIPTION_MAX_LENGTH } from '../../shared/constants/limits';
import { getAlertMessage } from '../../shared/constants/alertMessages';
import { useStandardAlertStore } from '../../shared/store/portal/alertStore';
import { bottomWithSafeArea } from '../../shared/constants/safeArea';
import { useBulk } from '../../shared/hooks/useApi/useBulk';
import { useBulkStore } from '../../shared/store/WorkListStore';
import { getImageDimensions, removeFileExtensionWithinLimit } from '../../shared/util/imageUtils';
import { filterUnsupportedImageFiles } from '../../shared/util/imageFileFormat';
import leftIcon from '../../asset/icon/left.png';
import upIcon from '../../asset/icon/up.png';
import { usePageOwnerIdentityStore } from '../../shared/store/pageOwnerIdentityStore';
import { isPageOwnerMatchedToRaw } from '../../shared/util/pageOwnerMatch';
import { useIsPageOwner } from '../../shared/hooks/useIsPageOwner';
import { canShowProjectElementList } from '../../shared/util/visibility/projectDetailVisibility';
import type { ResourceScope } from '../../shared/util/scopedResource';
import { useAuthStore, useAuthTokenStore } from '../../shared/store/authStore';
import { useValidation } from '../../shared/hooks/useValidation';


const CREATE_PE_DOCUMENT_INPUT_ID = 'project-element-create-document-input';
const CREATE_PE_PDF_INPUT_ID = 'project-element-create-pdf-input';

const ProjectElementList: React.FC = () => {
  const navigate = useNavigate();
  const { projectId, HANDLE } = useParams<{ projectId: string; HANDLE: string }>();
  const isOwner = useIsPageOwner();
  const hasAuthToken = useAuthTokenStore((s) => Boolean(s.authToken));
  const userAui = useAuthStore((s) => s.user?.aui);
  const { isEditMode } = useEditMode();
  const { project } = useProjectDetailState();
  const {
    projectElementList,
    listOwnerAui,
    listScope,
    listProjectId,
    status,
  } = useProjectElementState();
  const {
    getProjectElementList,
    getProjectElementListOwner,
    createProjectElement,
    updateProjectElementBulk,
    updateChangedProjectElementBulkInfo,
  } = useProjectElementActions();
  const hasChangedElements = projectElementList.some((pe) => pe.hasChanged);
  const { createPEBulk } = useBulk();
  const setTotal = useBulkStore((s) => s.setTotal);
  const clearCurrent = useBulkStore((s) => s.clearCurrent);
  const setStandardModal = useModalStore((s) => s.setStandardModal);
  const { deviceType } = useDeviceStore();
  const enqueueAlert = useStandardAlertStore((s) => s.enqueueAlert);
  const { isValidVideoUrl, isValidTextBlockLinkUrl } = useValidation();
  const pageOwner = usePageOwnerIdentityStore(s => s.pageOwner);
  const currentScope: ResourceScope = isOwner ? 'owner' : 'public';
  const canShowList = canShowProjectElementList({
    pageOwnerAui: pageOwner?.aui,
    resourceOwnerAui: listOwnerAui,
    resourceScope: listScope,
    currentScope,
    status,
    resourceProjectId: listProjectId,
    currentProjectId: projectId,
  });
  useEffect(() => {
    const getProjectElementListWithApi = async () => {
      if (!isPageOwnerMatchedToRaw(HANDLE, pageOwner)) return;
      if (!projectId) return;
      if (hasAuthToken && userAui == null) return;

      if (isOwner) {
        await getProjectElementListOwner(projectId);
      } else {
        await getProjectElementList(projectId);
      }
    }
    getProjectElementListWithApi();
  }, [HANDLE, pageOwner?.aui, projectId, isOwner, hasAuthToken, userAui]);

  if (!canShowList || !project) return null;

  const handleCreateElement = async (elementType: ProjectElementType) => {
    let newElement: CreateProjectElementReq;
    switch (elementType) {
      case ProjectElementType.TEXTBOX:
        newElement = peTextBoxBuilder(project.id);
        break;
      case ProjectElementType.FORMATION:
        newElement = peFormationBuilder(project.id);
        break;
      case ProjectElementType.CAROUSEL:
        newElement = peCarouselBuilder(project.id);
        break;
      case ProjectElementType.DIVIDER:
        newElement = peDividerBuilder(project.id);
        break;
      default:
        throw new Error(`Unsupported ProjectElementType: ${elementType}`);
    }
    await createProjectElement(newElement)
  };

  const handleImportWork = async () => {
    setStandardModal({
      modalType: ModalType.IMPORT_STATION_WORK,
      title: null,
      value: null,
      handleChange: () => { }
    });
  }
  const handleImportText = async () => {
    setStandardModal({
      modalType: ModalType.IMPORT_STATION_TEXT,
      title: null,
      value: null,
      handleChange: () => { }
    });
  }

  const handleReOrder = () => {
    setStandardModal({
      modalType: ModalType.INDEXING,
      title: "Element",
      value: null,
      handleChange: () => { },
    });
  }

  const handleUpdateChanged = async () => {
    const invalidVideo = projectElementList.find((pe) =>
      pe.hasChanged
      && pe.projectElementType === ProjectElementType.DOCUMENT
      && (pe.document.detailType ?? DetailType.IMAGE) === DetailType.VIDEO
      && !!pe.document.videoUrl
      && !isValidVideoUrl(pe.document.videoUrl),
    );
    if (invalidVideo) {
      document.getElementById(`project-element-document-${invalidVideo.id}`)?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
      return;
    }
    const invalidLink = projectElementList.find((pe) =>
      pe.hasChanged
      && pe.projectElementType === ProjectElementType.TEXT
      && pe.selectedTextBlocks.some((tb) =>
        tb.type === TextBlockType.LINK && !isValidTextBlockLinkUrl(tb.url || '')
      ),
    );
    if (invalidLink) {
      document.getElementById(`project-element-text-${invalidLink.id}`)?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
      return;
    }
    await updateChangedProjectElementBulkInfo();
  }

  const handleCreateProjectElement = () => {
    setStandardModal({
      modalType: ModalType.CREATE_PROJECT_ELEMENT_MOBILE,
      title: 'Add Content',
      value: null,
      handleChange: () => { }
    });
  }

  const createSingleDocumentFromFile = async (file: File) => {
    const results = await createPEBulk(
      [{ file, peType: ProjectElementType.DOCUMENT }],
      project.id,
      projectElementList.length,
    );
    if (!results?.length) return;
    const { width, height } = await getImageDimensions(file);
    await updateProjectElementBulk(project.id, {
      peList: [{
        peId: results[0].peId,
        projectElementType: ProjectElementType.DOCUMENT,
        originUrl: results[0].originUrl,
        width,
        height,
        description: removeFileExtensionWithinLimit(file.name, DOCUMENT_DESCRIPTION_MAX_LENGTH),
      }],
    });
  };


  const openDocumentFilePicker = () => {
    document.getElementById(CREATE_PE_DOCUMENT_INPUT_ID)?.click();
  };

  const openPDFFilePicker = () => {
    document.getElementById(CREATE_PE_PDF_INPUT_ID)?.click();
  };

  const handleCreateDocumentFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = filterUnsupportedImageFiles(Array.from(e.target.files ?? [])).filter((f) => f.type.startsWith('image/'));
    e.target.value = '';
    if (files.length === 0) return;

    if (files.length > BULK_UPLOAD_IMAGE_MAX_COUNT) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('BULK_UPLOAD_IMAGE_MAX_EXCEEDED', { maxCount: BULK_UPLOAD_IMAGE_MAX_COUNT }),
      });
      return;
    }

    if (files.length === 1) {
      createSingleDocumentFromFile(files[0]);
      return;
    }

    setTotal(files.length);
    clearCurrent();
    setStandardModal({
      modalType: ModalType.BULK_UPLOAD,
      title: 'Visual Aid',
      value: 'project-document',
      data: files,
      handleChange: () => { },
    });
  };

  const createPDFFromFiles = async (files: File[]) => {
    const results = await createPEBulk(
      files.map((file) => ({ file, peType: ProjectElementType.PDF })),
      project.id,
      projectElementList.length,
    );
    if (!results?.length) return;

    await updateProjectElementBulk(project.id, {
      peList: results.map((result, index) => ({
        peId: result.peId,
        projectElementType: ProjectElementType.PDF,
        originUrl: result.originUrl,
        fileSize: files[index]?.size,
        description: removeFileExtensionWithinLimit(files[index]?.name ?? '', DOCUMENT_DESCRIPTION_MAX_LENGTH),
      })),
    });
  };

  const handleCreatePDFFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []).filter(
      (f) => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf')
    );
    e.target.value = '';
    if (files.length === 0) return;

    createPDFFromFiles(files);
  };

  const handleDownloadPDFInMobile = () => {
    setStandardModal({
      modalType: ModalType.CREATE_PROJECT_PDF,
      title: null,
      value: null,
      handleChange: () => { }
    });
  }

  const renderConfirmButton = (transparent = false, keepMounted = false) => {
    if (!hasChangedElements && !keepMounted) return null;
    return (
      <HintWrapper content={getHintMessage('UPDATE_ELEMENTS')} toLeft={true}>
        <UniversalIconBtn
          isPrimary={true}
          size={transparent ? "fit" : "standard"}
          border={false}
          isTransparent={transparent}
          backgroundImg={confirmIcon}
          backgroundImgHover={confirmIcon2}
          onClick={handleUpdateChanged}
        />
      </HintWrapper>
    );
  };

  const renderCreateButton = () => {
    if (deviceType === DeviceType.MOBILE) {
      return (
        <>
          <HiddenFileInput
            id={CREATE_PE_DOCUMENT_INPUT_ID}
            type="file"
            accept="image/*"
            multiple
            onChange={handleCreateDocumentFilesChange}
          />
          <MobileContextToolbar
            hasChanged={hasChangedElements}
            confirmAction={renderConfirmButton(true, true)}
          >
            <UniversalIconBtn
              size="fit"
              border={false}
              isTransparent={true}
              backgroundImg={createIcon}
              onClick={handleCreateProjectElement} />
            <UniversalIconBtn
              size="fit"
              border={false}
              isTransparent={true}
              backgroundImg={reorderIcon}
              onClick={handleReOrder} />
            <UniversalIconBtn
              size="fit"
              border={false}
              isTransparent={true}
              backgroundImg={downloadIcon}
              onClick={handleDownloadPDFInMobile} />
          </MobileContextToolbar>
        </>
      );
    } else {
      return (
        <BtnContainer data-has-changed={hasChangedElements ? 'true' : 'false'}>
          <HiddenFileInput
            id={CREATE_PE_DOCUMENT_INPUT_ID}
            type="file"
            accept="image/*"
            multiple
            onChange={handleCreateDocumentFilesChange}
          />
          <HiddenFileInput
            id={CREATE_PE_PDF_INPUT_ID}
            type="file"
            accept="application/pdf"
            onChange={handleCreatePDFFilesChange}
          />
          <HintWrapper
            content={getHintMessage('ADD_WORK')}
            content2={getHintMessage('ADD_WORK_2')}
          >
            <HeadlessBtn
              value={"Work"}
              handleClick={handleImportWork}
              StyledBtn={PaperBlurFitBtn}
            />
          </HintWrapper>
          <HintWrapper
            content={getHintMessage('ADD_VISUAL_AID')}
            content2={getHintMessage('ADD_VISUAL_AID_2')}
          >
            <HeadlessBtn
              value={"Visual Aid"}
              handleClick={openDocumentFilePicker}
              StyledBtn={PaperBlurFitBtn}
            />
          </HintWrapper>
          <HintWrapper
            content={getHintMessage('ADD_TEXT')}
            content2={getHintMessage('ADD_TEXT_2')}
          >
            <HeadlessBtn
              value={"Text"}
              handleClick={handleImportText}
              StyledBtn={PaperBlurFitBtn}
            />
          </HintWrapper>
          <HintWrapper
            content={getHintMessage('ADD_DESCRIPTION')}
            content2={getHintMessage('ADD_DESCRIPTION_2')}
          >
            <HeadlessBtn
              value={"Description"}
              handleClick={() => handleCreateElement(ProjectElementType.TEXTBOX)}
              StyledBtn={PaperBlurFitBtn}
            />
          </HintWrapper>
          <HintWrapper
            content={getHintMessage('ADD_PDF')}
            content2={getHintMessage('ADD_PDF_2')}
          >
            <HeadlessBtn
              value={"PDF"}
              handleClick={openPDFFilePicker}
              StyledBtn={PaperBlurFitBtn}
            />
          </HintWrapper>
          <HintWrapper content={getHintMessage('ADD_FORMATION')}>
            <HeadlessBtn
              value={"Formation"}
              handleClick={() => handleCreateElement(ProjectElementType.FORMATION)}
              StyledBtn={PaperBlurFitBtn}
            />
          </HintWrapper>
          <HintWrapper content={getHintMessage('ADD_CAROUSEL')}>
            <HeadlessBtn
              value={"Carousel"}
              handleClick={() => handleCreateElement(ProjectElementType.CAROUSEL)}
              StyledBtn={PaperBlurFitBtn}
            />
          </HintWrapper>
          <HintWrapper content={getHintMessage('ADD_DIVIDER')}>
            <HeadlessBtn
              value={"Divider"}
              handleClick={() => handleCreateElement(ProjectElementType.DIVIDER)}
              StyledBtn={PaperBlurFitBtn}
            />
          </HintWrapper>
          <HintWrapper content={getHintMessage('REORDER_ELEMENTS')}>
            <UniversalIconBtn 
              size="standard" 
              border={false} 
              backgroundImg={reorderIcon} 
              onClick={handleReOrder} />
          </HintWrapper>
        </BtnContainer>
      );
    }
  }

  const handleDownloadPDF = () => {
    setStandardModal({
      modalType: ModalType.CREATE_PROJECT_PDF,
      title: null,
      value: null,
      handleChange: () => { }
    });
  }

  const handleCreateFloorPlan = () => {
    const hasWorkPe = projectElementList.some(
      (pe) => pe.projectElementType === ProjectElementType.WORK && pe.work != null
    );
    if (!hasWorkPe) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('ADD_WORK_BEFORE_FLOOR_PLAN'),
      });
      return;
    }
    setStandardModal({
      modalType: ModalType.CREATE_FLOOR_PLAN,
      title: null,
      value: null,
      handleChange: () => { }
    });
  }

  const handleNavigateToProjects = () => {
    if (!HANDLE) return;
    navigate(`/${HANDLE}/projects`);
  };

  const handleScrollProjectDetailToTop = () => {
    const main = document.querySelector('main');
    if (main instanceof HTMLElement) {
      main.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <ProjectElementListComp>
      {projectElementList.map((each, index) => (
        <ProjectElement
          key={each.id}
          data={each}
          priority={index < 2}
        />
      ))}
      {isEditMode && renderCreateButton()}
      {isEditMode && deviceType !== DeviceType.MOBILE && hasChangedElements && (
        <ConfirmBtnContainer data-has-changed="true">
          {renderConfirmButton()}
        </ConfirmBtnContainer>
      )}
      {isEditMode && deviceType !== DeviceType.MOBILE &&
        <CreateButtonGroup>
          <BtnSection
            onClick={handleDownloadPDF}>
            {getBtnName('PROJECT_PORTFOLIO')}
          </BtnSection>
          <BtnSection
            onClick={handleCreateFloorPlan}>
            {getBtnName('PROJECT_FLOOR_PLAN')}
          </BtnSection>
        </CreateButtonGroup>
      }
      {!isEditMode && (
          <BtnContainer2 $isMobile={deviceType === DeviceType.MOBILE}>
            <HintWrapper content={getHintMessage('NAVIGATE_TO_PROJECTS')}>
              <UniversalIconBtn
                size="fit"
                isTransparent={true}
                border={false}
                backgroundImg={leftIcon}
                onClick={handleNavigateToProjects}
              />
            </HintWrapper>
            <HintWrapper content={getHintMessage('SCROLL_PROJECT_DETAIL_TOP')}>
              <UniversalIconBtn
                size="fit"
                isTransparent={true}
                border={false}
                backgroundImg={upIcon}
                onClick={handleScrollProjectDetailToTop}
              />
            </HintWrapper>
          </BtnContainer2>
      )}
    </ProjectElementListComp>
  );
}

const ProjectElementListComp = styled.article`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: calc(4vh) 0 calc(8vh) 0;
  overflow-y: scroll;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const BtnContainer = styled.div`
  position: fixed;
  bottom: ${bottomWithSafeArea('clamp(30px, 7vw, 40px)')};
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  justify-content: center;
  gap: 0.5vh;
  z-index: 3;
`;

const ConfirmBtnContainer = styled.div`
  position: fixed;
  bottom: clamp(25px, 5vh, 50px);
  right: clamp(50px, 8vw, 70px);
  width: fit-content;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  z-index: 5;
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const CreateButtonGroup = styled.div`
  position: fixed;
  bottom: clamp(70px, 6vw, 100px);
  right: clamp(20px, 2vw, 30px);
  display: grid;
  grid-template-columns: max-content;
  width: fit-content;
  max-width: calc(100vw - 12vw);
  justify-content: end;
  align-content: center;
  gap: 0;
  box-sizing: border-box;
  z-index: 4;
  ${paperTextureAlert}
`;



/** 모바일: 한 장의 종이 질감 안에 액션을 가로로 두 섹션으로 나누고, 사이에 세로 구분선 */
const CreateButtonGroupMobile = styled.div`
  position: fixed;
  bottom: ${bottomWithSafeArea('clamp(30px, 7vw, 40px)')};
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: row;
  align-items: stretch;
  width: fit-content;
  max-width: calc(100vw - 12vw);
  box-sizing: border-box;
  ${paperTextureAlert}
`;

const BtnSection = styled.div`
  position: relative;
  padding: 8px 10px;
  ${({ theme }) => theme.typography.Body_04};
  &:not(:last-child)::after {
    content: '';
    position: absolute;
    left: 5%;
    right: 5%;
    bottom: 0;
    height: 1px;
    background: linear-gradient(
      to right,
      transparent,
      ${({ theme }) => theme.colors.color_Gray_06} 15%,
      ${({ theme }) => theme.colors.color_Gray_06} 95%,
      transparent
    );
  }
  &:hover {
    transform: scale(0.95);
  }
  transition: all 0.3s ease-in-out;
  cursor: pointer;
`;


const BtnContainer2 = styled.div<{ $isMobile: boolean }>`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 1vh;

  padding: ${({ $isMobile }) => $isMobile ? '0 clamp(18px, 5vw, 24px)' : '0 calc(8vw)'};
  padding-bottom: 20px;
`

export default ProjectElementList;
